/**
 * Backup & Restore admin page (platform_admin / platform_owner only).
 *
 * Shipped 11-May-2026.
 *
 * Features:
 *  - Backup history table (newest first)
 *  - Drive connectivity health card
 *  - "Run backup now" button
 *  - Restore — staged for a future release; UI placeholder with the
 *    safety pattern (typed confirmation modal) already in place.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { ArrowsClockwise, CloudArrowUp, Database, FileZip,
         CheckCircle, XCircle, Spinner, Trash, Link as LinkIcon,
         Warning, Clock as ClockIcon } from "@phosphor-icons/react";

function bytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function duration(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60), s = seconds % 60;
  return `${m}m ${s}s`;
}

function StatusBadge({ status }) {
  const map = {
    success:     { c: "#16A34A", icon: CheckCircle, label: "Success" },
    failed:      { c: "#DC2626", icon: XCircle,     label: "Failed" },
    in_progress: { c: "#0055FF", icon: Spinner,     label: "Running…" },
  };
  const v = map[status] || { c: "#525252", icon: ClockIcon, label: status };
  const Icon = v.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded"
          style={{ background: `${v.c}15`, color: v.c }}
          data-testid={`backup-status-${status}`}>
      <Icon size={11} weight="bold" />
      {v.label}
    </span>
  );
}

export default function BackupsPage() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [drive, setDrive] = useState(null);
  const [driveError, setDriveError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);

  const role = (user?.role || "").toLowerCase();
  const isAdmin = role === "platform_owner" || role === "platform_admin";
  const isOwner = role === "platform_owner";

  const load = async () => {
    setLoading(true);
    try {
      const [h, d] = await Promise.all([
        api.get("/admin/backups"),
        api.get("/admin/backups/drive-status").catch(e => ({ data: { error: e.response?.data?.detail || e.message } })),
      ]);
      setHistory(h.data.backups || []);
      if (d.data?.error) {
        setDriveError(d.data.error);
        setDrive(null);
      } else {
        setDrive(d.data);
        setDriveError(null);
      }
    } catch (e) {
      toast.error("Could not load backups: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  // Poll for "in_progress" rows every 5s so the UI auto-refreshes
  useEffect(() => {
    if (!history.some(h => h.status === "in_progress")) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [history]);

  const triggerBackup = async () => {
    if (triggering) return;
    setTriggering(true);
    try {
      await api.post("/admin/backups/run");
      toast.success("Backup started — refresh in a minute to see progress");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error("Backup trigger failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setTriggering(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="backup-denied">
        <div className="bg-[#FFF1F1] border border-[#DC2626] p-4 text-sm text-[#0A0A0A]">
          <strong>Access denied.</strong> Backup &amp; Restore is restricted to
          Platform Administrators and Platform Owners.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="backup-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[2px] text-[#A3A3A3] mb-1">
            Platform Administration
          </div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Backup &amp; Restore</h1>
          <div className="text-sm text-[#525252] mt-1">
            Daily automated backups upload a full DB + object-files zip to Google Drive at 2:00 AM PT.
            You can also trigger an immediate backup any time below.
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#E5E5E5] text-[#525252] text-xs uppercase tracking-wider hover:bg-[#FAFAFA]"
            data-testid="backup-refresh-btn"
          >
            <ArrowsClockwise size={12} weight="bold" /> Refresh
          </button>
          <button
            onClick={triggerBackup}
            disabled={triggering}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#003ECC] disabled:opacity-50"
            data-testid="backup-trigger-btn"
          >
            {triggering ? <Spinner size={12} className="animate-spin" /> : <CloudArrowUp size={12} weight="bold" />}
            Run backup now
          </button>
        </div>
      </div>

      {/* Drive health card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <DriveCard drive={drive} driveError={driveError} />
        <StatCard
          icon={Database}
          label="Total backups"
          value={history.length}
          subtitle={`${history.filter(h => h.status === "success").length} successful`}
        />
        <StatCard
          icon={FileZip}
          label="Latest backup size"
          value={bytes(history.find(h => h.status === "success")?.size_bytes)}
          subtitle={history.find(h => h.status === "success") ?
            `Generated ${fmtDateTime(history.find(h => h.status === "success").completed_at)}` :
            "No successful backup yet"}
        />
      </div>

      {/* History table */}
      <div className="border border-[#E5E5E5] bg-white">
        <div className="bg-[#FAFAFA] px-4 py-2 border-b border-[#E5E5E5] flex items-center justify-between">
          <div className="font-bold text-sm text-[#0A0A0A]">Backup history</div>
          <div className="text-[10px] uppercase tracking-[2px] text-[#525252]">{history.length} total</div>
        </div>
        {loading && history.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#A3A3A3]">Loading…</div>
        ) : history.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#A3A3A3]" data-testid="backup-empty">
            No backups yet. Click <strong>Run backup now</strong> above to create one.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="backup-history-table">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-[2px] text-[#525252]">
              <tr>
                <th className="text-left  px-4 py-2 border-b border-[#E5E5E5] w-44">Started</th>
                <th className="text-left  px-4 py-2 border-b border-[#E5E5E5] w-28">Status</th>
                <th className="text-right px-4 py-2 border-b border-[#E5E5E5] w-24">Size</th>
                <th className="text-right px-4 py-2 border-b border-[#E5E5E5] w-20">Tables</th>
                <th className="text-right px-4 py-2 border-b border-[#E5E5E5] w-20">Rows</th>
                <th className="text-right px-4 py-2 border-b border-[#E5E5E5] w-24">Files</th>
                <th className="text-right px-4 py-2 border-b border-[#E5E5E5] w-24">Duration</th>
                <th className="text-left  px-4 py-2 border-b border-[#E5E5E5]">Triggered by</th>
                <th className="text-center px-4 py-2 border-b border-[#E5E5E5] w-24">Drive</th>
              </tr>
            </thead>
            <tbody>
              {history.map(b => (
                <tr key={b.id} className="hover:bg-[#FAFAFA]" data-testid={`backup-row-${b.id}`}>
                  <td className="px-4 py-2 border-b border-[#F5F5F5] font-mono text-xs">
                    {fmtDateTime(b.started_at)}
                  </td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5]">
                    <StatusBadge status={b.status} />
                    {b.status === "failed" && b.error_message && (
                      <div className="text-[10px] text-[#DC2626] mt-1 truncate max-w-[200px]" title={b.error_message}>
                        {b.error_message.substring(0, 60)}…
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5] text-right font-mono text-xs">{bytes(b.size_bytes)}</td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5] text-right font-mono text-xs">{b.table_count ?? "—"}</td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5] text-right font-mono text-xs">{b.total_rows?.toLocaleString() ?? "—"}</td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5] text-right font-mono text-xs">
                    {b.file_count != null ? (
                      <span>
                        {b.file_count}
                        {b.files_failed > 0 && (
                          <span className="text-[#D97706]" title={`${b.files_failed} files failed to fetch`}>
                            {` /${b.files_failed} fail`}
                          </span>
                        )}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5] text-right font-mono text-xs">{duration(b.duration_seconds)}</td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5] text-xs">
                    {b.triggered_by_email || "—"}
                    <div className="text-[10px] text-[#A3A3A3]">
                      {b.triggered_via === "scheduled" ? "Scheduled" : "Manual"}
                    </div>
                  </td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5] text-center">
                    {b.drive_web_link ? (
                      <a href={b.drive_web_link} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-[#0055FF] hover:underline text-xs"
                         data-testid={`backup-drive-link-${b.id}`}>
                        <LinkIcon size={11} weight="bold" />
                        Open
                      </a>
                    ) : <span className="text-[#A3A3A3] text-xs">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Restore section — placeholder until the restore engine ships */}
      <div className="mt-8 border border-[#D97706] bg-[#FFF7EE] p-4" data-testid="restore-section">
        <div className="flex items-start gap-3">
          <Warning size={20} weight="fill" className="text-[#D97706] mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-bold text-sm text-[#0A0A0A]">Restore (Platform Owner only)</div>
            <div className="text-xs text-[#525252] mt-1">
              The restore flow will let you re-create the entire production DB and object-store
              state from any backup zip in the Drive history above. It is destructive — the
              current live data is replaced. Implementation is scheduled for the next release;
              for now you can manually restore by downloading the zip from Drive and following
              the README.md inside.
            </div>
            {!isOwner && (
              <div className="text-[11px] text-[#D97706] mt-2 font-bold">
                Note: When restore ships, only Platform Owners will be able to trigger it.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DriveCard({ drive, driveError }) {
  if (driveError) {
    return (
      <div className="border border-[#DC2626] bg-[#FFF1F1] p-4" data-testid="drive-status-error">
        <div className="flex items-start gap-2">
          <XCircle size={16} weight="fill" className="text-[#DC2626] mt-0.5" />
          <div>
            <div className="font-bold text-xs uppercase tracking-wider text-[#DC2626]">Drive offline</div>
            <div className="text-[11px] text-[#525252] mt-1 break-all">{driveError}</div>
          </div>
        </div>
      </div>
    );
  }
  if (!drive) return <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-4 animate-pulse">Checking Drive…</div>;
  return (
    <div className="border border-[#16A34A] bg-[#F0FDF4] p-4" data-testid="drive-status-ok">
      <div className="flex items-start gap-2">
        <CheckCircle size={16} weight="fill" className="text-[#16A34A] mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-xs uppercase tracking-wider text-[#16A34A]">Drive connected</div>
          <div className="text-[11px] text-[#525252] mt-1 truncate" title={drive.shared_drive_name}>
            {drive.shared_drive_name}
          </div>
          <div className="text-[10px] text-[#A3A3A3] mt-0.5 truncate" title={drive.service_account_email}>
            {drive.service_account_email}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subtitle }) {
  return (
    <div className="border border-[#E5E5E5] bg-white p-4">
      <div className="flex items-start gap-2">
        <Icon size={16} weight="bold" className="text-[#525252] mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-[#A3A3A3]">{label}</div>
          <div className="text-xl font-bold text-[#0A0A0A] mt-0.5">{value}</div>
          <div className="text-[10px] text-[#A3A3A3] mt-0.5 truncate" title={subtitle}>{subtitle}</div>
        </div>
      </div>
    </div>
  );
}
